import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { get } from 'lodash';
import { GOOGLE_ADS_CLICK_IDENTIFIERS, QUERY_SEARCH_STRING, setReferralCode } from './localStorage';
import { ORGANIZATION_TYPE_OPTIONS } from '../scenes/V2/CreateAccountScene/OrganizationType/OrganizationType';

/**
 * Whitelist of routes that should capture query parameters
 * Add new routes here to enable automatic query parameter capture
 */
export const QUERY_PARAM_CAPTURE_WHITELIST = [
  '/create-account',
  '/create-account/role',
  '/create-account/organization-type',
  '/create-account/sign-up',
];

/**
 * Custom hook to automatically capture query parameters from URL
 * and store them in localStorage for use throughout the registration flow
 *
 * @param {Object} options - Configuration options
 * @param {boolean} options.enabled - Whether to enable capture (default: true)
 * @param {boolean} options.checkWhitelist - Whether to check if current route is whitelisted (default: true)
 * @returns {Object} - Object containing searchParams string
 *
 * @example
 * // In a component
 * function MyComponent() {
 *   useQueryParamCapture();
 *   // Query params are now automatically captured and stored
 * }
 */
export const useQueryParamCapture = (options = {}) => {
  const location = useLocation();
  const { enabled = true, checkWhitelist = true } = options;

  useEffect(() => {
    if (!enabled) return;

    // Check if current route is whitelisted
    const currentPath = location.pathname;
    const isWhitelisted = QUERY_PARAM_CAPTURE_WHITELIST.some(
      route => currentPath === route || currentPath.startsWith(route + '/')
    );

    if (checkWhitelist && !isWhitelisted) {
      return; // Skip capture if not whitelisted
    }

    // Capture query parameters from URL
    const searchParams = get(location, 'search', '');

    if (searchParams) {
      const params = new URLSearchParams(searchParams);

      // Extract and store mwr parameter separately for reliable campaign identification
      const mwrParam = params.get('mwr');
      if (mwrParam) {
        setReferralCode(mwrParam);
      }

      // Extract and store orgType parameter if valid
      const orgTypeParam = params.get('orgType');
      if (orgTypeParam) {
        const isValidOrgType = ORGANIZATION_TYPE_OPTIONS.some(option => option.value === orgTypeParam);
        if (isValidOrgType) {
          localStorage.setItem('registrationOrgType', orgTypeParam);
        }
      }

      // Store in GOOGLE_ADS_CLICK_IDENTIFIERS for tracking params (gclid, etc.)
      localStorage.setItem(
        GOOGLE_ADS_CLICK_IDENTIFIERS,
        JSON.stringify({
          searchParams,
          searchParamsSet: false,
          oauthSearchParamsSet: false,
        })
      );

      // Store in QUERY_SEARCH_STRING for form field pre-fill params
      localStorage.setItem(
        QUERY_SEARCH_STRING,
        JSON.stringify({
          searchParams,
          searchParamsSet: false,
        })
      );
    }
  }, [location, enabled, checkWhitelist]);

  return {
    searchParams: location.search,
  };
};

/**
 * Utility function to add a route to the whitelist at runtime
 * (Note: This is mainly for documentation - prefer updating the constant directly)
 *
 * @param {string} route - Route path to add to whitelist
 */
export const addToWhitelist = route => {
  if (!QUERY_PARAM_CAPTURE_WHITELIST.includes(route)) {
    QUERY_PARAM_CAPTURE_WHITELIST.push(route);
  }
};

/**
 * Check if a route is whitelisted for query param capture
 *
 * @param {string} route - Route path to check
 * @returns {boolean} - Whether the route is whitelisted
 */
export const isRouteWhitelisted = route => {
  return QUERY_PARAM_CAPTURE_WHITELIST.some(
    whitelistedRoute => route === whitelistedRoute || route.startsWith(whitelistedRoute + '/')
  );
};

/**
 * Higher-Order Component (HOC) to add query parameter capture to class components
 * Wraps a class component and automatically captures query params if route is whitelisted
 *
 * @param {React.Component} WrappedComponent - The class component to wrap
 * @param {Object} options - Configuration options (same as useQueryParamCapture)
 * @returns {React.Component} - Enhanced component with query param capture
 *
 * @example
 * // In a class component file
 * class MyClassComponent extends Component {
 *   render() {
 *     return <div>My content</div>;
 *   }
 * }
 *
 * export default withQueryParamCapture(MyClassComponent);
 */
export const withQueryParamCapture = (WrappedComponent, options = {}) => {
  const WithQueryParamCapture = props => {
    // Use the hook to capture query parameters
    useQueryParamCapture(options);

    // Render the wrapped component with all its props
    return <WrappedComponent {...props} />;
  };

  // Set display name for debugging
  WithQueryParamCapture.displayName = `WithQueryParamCapture(${
    WrappedComponent.displayName || WrappedComponent.name || 'Component'
  })`;

  return WithQueryParamCapture;
};
